import { useState } from 'react';
import { Calendar, CheckCircle, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

export function DemoSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setStatus('idle');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.company) {
      setStatus('error');
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    // Store in localStorage for MVP
    const demoRequests = JSON.parse(localStorage.getItem('positioniq_demo_requests') || '[]');
    demoRequests.push({
      ...formData,
      submittedAt: new Date().toISOString(),
    });
    localStorage.setItem('positioniq_demo_requests', JSON.stringify(demoRequests));

    console.log('Demo request:', formData);
    setStatus('success');
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <section id="demo" className="relative py-20 md:py-28 bg-slate-50 overflow-hidden">
      {/* Subtle grid texture background */}
      <div className="absolute inset-0 opacity-[0.4]" aria-hidden="true">
        <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="demo-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#demo-grid)" />
        </svg>
      </div>

      <div className="container relative">
        <div className="mx-auto max-w-2xl">
          <div className="text-center mb-12">
            <div className="inline-flex rounded-full bg-purple-600 p-4 mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-4 text-slate-900">
              Schedule a Demo
            </h2>
            <p className="text-slate-600 max-w-lg mx-auto">
              See PositionIQ in action. Our team will walk you through the platform and answer your questions.
            </p>
          </div>

          {status === 'success' ? (
            <div className="rounded-2xl bg-white border border-slate-200 shadow-lg p-8 text-center">
              <div className="inline-flex rounded-full bg-emerald-100 border border-emerald-200 p-4 mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-900">Demo Request Received!</h3>
              <p className="text-slate-600">
                Thank you for your interest. We'll reach out within 1-2 business days to schedule your demo.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl bg-white border border-slate-200 shadow-lg p-6 md:p-8">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2 text-slate-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-slate-700">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="company" className="block text-sm font-medium mb-2 text-slate-700">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Trading LLC"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-sm font-medium mb-2 text-slate-700">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your compliance needs..."
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none"
                  />
                </div>
              </div>

              {status === 'error' && (
                <div className="flex items-center gap-2 mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                className="mt-6 w-full rounded-xl bg-purple-600 px-6 py-4 text-base font-semibold text-white hover:bg-purple-700 transition-colors"
              >
                Request Demo
              </button>

              <p className="mt-4 text-xs text-center text-slate-500">
                By submitting, you agree to receive communications from PositionIQ.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
